//#region types
import { RouteConstants, ServiceConstants } from "utils/constants";
import { CommentsFormTypes, IncomingInvoiceFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { AuthEnums, StyleEnums, IncomingInvoiceEnums } from "models/enums";
import {
  WintSuperkollAPIModel as ApiModels,
  WintSuperkollAPIModelAgingIncomingInvoices as AgingInvoiceApiModel,
  WintUtilsWebApi,
} from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import {
  AgingIncomingInvoiceOverPaidActions,
  AgingIncomingInvoiceUnderPaidActions,
} from "models/enums/incomingInvoice";
//#endregion
//#region components
import useAPIClientHelper from "./useAPIClientHelper";
import { getUpdatedInvoice } from "components/incomingInvoice/incomingInvoicePutModel";
//#endregion
import { LocalizationHelperInstance, DateHelper, ServiceHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useIncomingInvoiceService() {
  const { apiClientInstance } = useAPIClientHelper();
  const editableStates = [ApiModels.IncomingInvoiceState.Received, ApiModels.IncomingInvoiceState.Signee];
  const get = (id: number, key?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.Get(id)
      .then((result) => ({ ...result, key: key }));
  };
  const save = (id: number, invoice: ApiModels.IncomingInvoicePut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).IncomingInvoiceClient.Put(id, invoice);
  };
  const create = (invoice: ApiModels.IncomingInvoicePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).IncomingInvoiceClient.Post(invoice);
  };
  const certify = (invoice: ApiModels.IncomingInvoice) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.UpdateAndCertify(invoice.id, invoice as ApiModels.IncomingInvoicePut);
  };
  const sign = (invoice: ApiModels.IncomingInvoice) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.UpdateAndSign(invoice.id, invoice as ApiModels.IncomingInvoicePut);
  };
  const onAccept = (item: ApiModels.IncomingInvoice) => {
    getPeriodisationsAllowed(item.amount, item.currency ?? "", item.invoiceDate).then((res) => {
      if (!res) {
        item.periodisationStart = undefined;
        item.periodisationEnd = undefined;
      }
    });

    const action = item.state === ApiModels.IncomingInvoiceState.Received ? certify : sign;

    return action(item);
  };
  const onUpdatePaymentDetails = (id: number, item: ApiModels.SupplierPaymentDetails) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.UpdatePaymentDetails(id, item);
  };

  const onUpdateSupplierPostalAddress = (id: number, item: ApiModels.SupplierAddressDetails) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.UpdateSupplierAddressDetails(id, item);
  };

  const onAgingInvoiceSubmit = (updateInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AgingIncomingInvoiceClient.UpdateAgingIncomingInvoice(updateInfo);
  };
  const cancel = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.Cancel(id);
  };
  const sendToPerson = (id: number, personId: number, comment: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.SendToPerson(id, {
      personId: personId,
      comment: comment,
    } as ApiModels.IncomingInvoiceSendToPersonAction);
  };
  const sendBack = (id: number, email: string, subject: string, message: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.SendBack(id, {
      email: email,
      subject: subject,
      message: message,
    } as ApiModels.IncomingInvoiceSendBackAction);
  };
  const toggleAlreadyPaid = (id: number, isAlreadyPaid?: boolean, paymentDate?: Date) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.ToggleAlreadyPaid(id, {
      isAlreadyPaid: isAlreadyPaid,
      paymentDate: paymentDate,
    });
  };
  const convertToGenericVoucher = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.ConvertToGenericVoucher(id);
  };
  const getPdf = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).IncomingInvoiceClient.GetPdf(id);
  };
  const getAttachment = (invoiceId: number, attachmentId: number | string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetAttachmentAsync(invoiceId, attachmentId as number)
      .then((res) => res.data.data ?? "");
  };
  const getIncomingInvoicePersons = (searchStr: string, includeSelf?: boolean) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetIncomingInvoicePersons({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        includeSelf: includeSelf ?? false,
      } as ApiModels.IncomingInvoicePersonsFilter)
      .then((res) =>
        res.data.items
          ? res.data.items.map(
              (receiver): WintUtilsWebApi.SearchResult => ({ id: receiver.id, name: receiver.name ?? "" })
            )
          : []
      );
  };
  const getSuggestedIncomingInvoicePersons = (includeSelf?: boolean) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetSuggestedIncomingInvoicePersons({
        count: ServiceConstants.SuggestionsCount,
        includeSelf: includeSelf ?? false,
      })
      .then((res) => (res.data.items ? res.data.items : []));
  };
  const getStateText = (state: IncomingInvoiceEnums.ViewState) => {
    return LocalizationHelperInstance.getFormattedMessage(`IncomingInvoice.ViewState_${state}`);
  };
  const getPaymentStateText = (state: ApiModels.IncomingInvoicePaymentStatus) => {
    return LocalizationHelperInstance.getFormattedMessage(`IncomingInvoice.PaymentStatus_${state}`);
  };
  const getStateFlowText = (state: ApiModels.IncomingInvoiceState, hasSignee: boolean = false) => {
    let transformedState = state;
    if (hasSignee) {
      switch (state) {
        case ApiModels.IncomingInvoiceState.Signee:
          transformedState = ApiModels.IncomingInvoiceState.Accepted;
          break;
        case ApiModels.IncomingInvoiceState.Accepted:
          transformedState = ApiModels.IncomingInvoiceState.Signee;
          break;
      }
    }

    return LocalizationHelperInstance.getFormattedMessage(`IncomingInvoice.StateFlowState_${transformedState}`);
  };
  const getStateLogs = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetStateLogs(id)
      .then((res) => res.data);
  };
  const getTransactions = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetTransactions(id)
      .then((res) => res.data);
  };
  const getSimilarInvoices = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.FindSimilar(id, 7)
      .then((res) => res.data);
  };
  const getDueIn = (dueDate?: Date) => {
    return dueDate ? DateHelper.getDiffDays(new Date(), dueDate) : undefined;
  };
  const getOcrCollisions = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetOCRCollisions(id)
      .then((res) => res.data);
  };
  const getUIDisplayState = (
    state: ApiModels.IncomingInvoiceState,
    paymentState: ApiModels.IncomingInvoicePaymentStatus,
    isValid?: boolean
  ) => {
    let displayType;
    let hasDueDateWarning = false;

    if (
      state === ApiModels.IncomingInvoiceState.Unhandled ||
      state === ApiModels.IncomingInvoiceState.SentForProcessing
    ) {
      return {
        displayType: StyleEnums.DisplayType.Info,
        hasDueDateWarning: false,
      };
    }

    if (
      state === ApiModels.IncomingInvoiceState.SentBack ||
      state === ApiModels.IncomingInvoiceState.Denied ||
      state === ApiModels.IncomingInvoiceState.DeniedAndHandled ||
      state === ApiModels.IncomingInvoiceState.DeniedAndUnhandled
    ) {
      displayType = StyleEnums.DisplayType.None;
    } else if (state === ApiModels.IncomingInvoiceState.Paid) {
      displayType = StyleEnums.DisplayType.Success;
    } else if (state === ApiModels.IncomingInvoiceState.Accepted || state === ApiModels.IncomingInvoiceState.Booked) {
      displayType = StyleEnums.DisplayType.Info;
    } else if (paymentState === ApiModels.IncomingInvoicePaymentStatus.Due) {
      hasDueDateWarning = true;
      displayType = StyleEnums.DisplayType.Danger;
    } else if (paymentState === ApiModels.IncomingInvoicePaymentStatus.DueSoon) {
      hasDueDateWarning = true;
      displayType = StyleEnums.DisplayType.Warning;
    } else {
      displayType = StyleEnums.DisplayType.Info;
    }

    return {
      displayType:
        displayType === StyleEnums.DisplayType.Info && isValid ? StyleEnums.DisplayType.Success : displayType,
      hasDueDateWarning: hasDueDateWarning,
    };
  };
  const allowPeriodisations = (amount: number, currency?: string, invoiceDate?: Date) => {
    return checkPeriodisationAllowed({
      invoiceTotalAmount: amount,
      currency: currency,
      invoicedDate: invoiceDate,
    });
  };
  const getPeriodisationsAllowed = (amount: number, currency?: string, invoiceDate?: Date) => {
    return checkPeriodisationAllowed({
      invoiceTotalAmount: amount,
      currency: currency,
      invoicedDate: invoiceDate,
    });
  };
  const getAgingIncomingInvoiceOverPaidApiAction = (
    action?: AgingIncomingInvoiceOverPaidActions | AgingIncomingInvoiceUnderPaidActions
  ) => {
    switch (action) {
      case AgingIncomingInvoiceOverPaidActions.OverpaidAwaitingRefund:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.OverpaidAwaitingRefund;
      case AgingIncomingInvoiceOverPaidActions.OverpaidInfoSupplied:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.OverpaidInfoSupplied;
      default:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.None;
    }
  };
  const getAgingIncomingInvoiceUnderPaidApiAction = (action?: AgingIncomingInvoiceUnderPaidActions) => {
    switch (action) {
      case AgingIncomingInvoiceUnderPaidActions.UnderpaidWithholdPaymentIntentionally:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidWithholdPaymentIntentionally;
      case AgingIncomingInvoiceUnderPaidActions.UnderpaidHasBeenPaidInfoSupplied:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidHasBeenPaidInfoSupplied;
      case AgingIncomingInvoiceUnderPaidActions.UnderpaidCreatePayment:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidCreatePayment;
      case AgingIncomingInvoiceUnderPaidActions.UnderpaidOtherInfoSupplied:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidOtherInfoSupplied;
      default:
        return AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.None;
    }
  };
  const getOverPaidInvoiceApiModel = (
    agingInvoiceId: number,
    values: IncomingInvoiceFormTypes.OverPaidAgingInvoiceTodo
  ) => {
    const baseInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo = {
      agingIncomingInvoiceId: agingInvoiceId,
      action: getAgingIncomingInvoiceOverPaidApiAction(values.action),
    };

    switch (baseInfo.action) {
      case AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.OverpaidAwaitingRefund:
        return baseInfo;
      case AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.OverpaidInfoSupplied: {
        const updatedInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo = {
          ...baseInfo,
          overpaidInfo: {
            message: values.explanation,
            images:
              (
                values as IncomingInvoiceFormTypes.OverPaidAgingInvoiceTodo
              ).files?.map<AgingInvoiceApiModel.AgingInvoiceImage>((item) => ({
                fileName: item.fileName,
                data: item.data,
                contentType: item.contentType,
              })) ?? [],
          },
        };
        return updatedInfo;
      }
      default:
        return baseInfo;
    }
  };
  const getUnderPaidInvoiceApiModel = (
    agingInvoiceId: number,
    values: IncomingInvoiceFormTypes.UnderPaidAgingInvoiceTodo
  ) => {
    const baseInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo = {
      agingIncomingInvoiceId: agingInvoiceId,
      action: getAgingIncomingInvoiceUnderPaidApiAction(values.action),
    };

    switch (baseInfo.action) {
      case AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidHasBeenPaidInfoSupplied: {
        const updatedInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo = {
          ...baseInfo,
          alreadyPaidInfo: {
            paymentDate: (values as IncomingInvoiceFormTypes.UnderPaidAgingInvoiceTodo).paymentDate!,
            message: values.explanation,
            images: [],
          },
        };
        return updatedInfo;
      }
      case AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidOtherInfoSupplied: {
        const updatedInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo = {
          ...baseInfo,
          creditedInfo: {
            message: values.explanation,
            images: [],
          },
        };
        return updatedInfo;
      }
      case AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidCreatePayment: {
        const updatedInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo = {
          ...baseInfo,
          createPaymentInfo: {
            paymentDate: (values as IncomingInvoiceFormTypes.UnderPaidAgingInvoiceTodo).plannedPaymentDate!,
          },
        };
        return updatedInfo;
      }
      case AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.UnderpaidWithholdPaymentIntentionally:
        return baseInfo;
      default:
        return baseInfo;
    }
  };
  const getAgingInvoiceApiModel = (
    todoType: ApiModels.TodoSubType,
    isQuestion: boolean,
    agingInvoiceId: number,
    values:
      | IncomingInvoiceFormTypes.OverPaidAgingInvoiceTodo
      | IncomingInvoiceFormTypes.UnderPaidAgingInvoiceTodo
      | CommentsFormTypes.CommentWithFile
  ) => {
    if (isQuestion) {
      return getAgingInvoiceFilesAndExplanation(agingInvoiceId, values as CommentsFormTypes.CommentWithFile);
    }

    switch (todoType) {
      case ApiModels.TodoSubType.AgingIncomingInvoiceOverPaid:
        return getOverPaidInvoiceApiModel(agingInvoiceId, values as IncomingInvoiceFormTypes.OverPaidAgingInvoiceTodo);
      case ApiModels.TodoSubType.AgingIncomingInvoiceLeftToPay:
        return getUnderPaidInvoiceApiModel(
          agingInvoiceId,
          values as IncomingInvoiceFormTypes.UnderPaidAgingInvoiceTodo
        );
      default:
        return {
          agingIncomingInvoiceId: agingInvoiceId,
          action: AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.None,
        };
    }
  };
  const getAgingInvoiceFilesAndExplanation = (agingInvoiceId: number, values: CommentsFormTypes.CommentWithFile) => {
    const files =
      values.filesToUpload?.map(
        (file): AgingInvoiceApiModel.AgingInvoiceImage => ({
          fileName: file.fileName,
          data: file.data,
          contentType: file.contentType,
        })
      ) ?? [];

    const updatedInfo: AgingInvoiceApiModel.AgingIncomingInvoiceUpdateInfo = {
      agingIncomingInvoiceId: agingInvoiceId,
      action: AgingInvoiceApiModel.AgingIncomingInvoiceApiAction.InfoSupplied,
      info: {
        message: values.explanation,
        images: files,
      },
    };

    return updatedInfo;
  };

  const getUpdatedPaymentDetails = (values: any): ApiModels.SupplierPaymentDetails => {
    return {
      iBAN: values.iban,
      bIC: values.bic,
    };
  };

  const getUpdatedSupplierPostalAddress = (values: any): ApiModels.SupplierAddressDetails => {
    return {
      country: values.country,
      city: values.city,
      zipCode: values.zipCode,
      street: values.street,
      street2: values.street2,
    };
  };

  const toViewState = (apiState: ApiModels.IncomingInvoiceState) => {
    switch (apiState) {
      case ApiModels.IncomingInvoiceState.Received:
        return IncomingInvoiceEnums.ViewState.ToApprove;
      case ApiModels.IncomingInvoiceState.Unhandled:
      case ApiModels.IncomingInvoiceState.SentForProcessing:
        return IncomingInvoiceEnums.ViewState.Unhandled;
      case ApiModels.IncomingInvoiceState.Signee:
        return IncomingInvoiceEnums.ViewState.ToFinalApproval;
      case ApiModels.IncomingInvoiceState.Accepted:
        return IncomingInvoiceEnums.ViewState.InProcess;
      case ApiModels.IncomingInvoiceState.Booked:
      case ApiModels.IncomingInvoiceState.PaymentSign:
      case ApiModels.IncomingInvoiceState.Stopped:
      case ApiModels.IncomingInvoiceState.PayoutRescheduled:
      case ApiModels.IncomingInvoiceState.Paid:
      case ApiModels.IncomingInvoiceState.PaymentProcessing:
        return IncomingInvoiceEnums.ViewState.InBook;
      case ApiModels.IncomingInvoiceState.Denied:
      case ApiModels.IncomingInvoiceState.DeniedAndHandled:
      case ApiModels.IncomingInvoiceState.SentBack:
      case ApiModels.IncomingInvoiceState.DeniedAndUnhandled:
        return IncomingInvoiceEnums.ViewState.Cancelled;
    }
  };

  const getSuppliers = () => {
    return ServiceHelper.getAllPages((page) =>
      apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.GetSuppliers({
        ...ServiceConstants.DefaultFilter,
        orderByProperty: "Id",
        page: page,
      })
    );
  };

  const searchSuppliers = (name?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetSuppliers({
        ...ServiceConstants.DefaultFilter,
        orderByProperty: "Name",
        name: name || undefined,
      })
      .then((res) => res.data?.items ?? []);
  };

  const toApiStates = (viewState: IncomingInvoiceEnums.ViewState) => {
    switch (viewState) {
      case IncomingInvoiceEnums.ViewState.ToApprove:
        return [ApiModels.IncomingInvoiceState.Received];
      case IncomingInvoiceEnums.ViewState.ToFinalApproval:
        return [ApiModels.IncomingInvoiceState.Signee];
      case IncomingInvoiceEnums.ViewState.InProcess:
        return [ApiModels.IncomingInvoiceState.Accepted];
      case IncomingInvoiceEnums.ViewState.InBook:
        return [
          ApiModels.IncomingInvoiceState.Booked,
          ApiModels.IncomingInvoiceState.PaymentSign,
          ApiModels.IncomingInvoiceState.Stopped,
          ApiModels.IncomingInvoiceState.PayoutRescheduled,
          ApiModels.IncomingInvoiceState.Paid,
          ApiModels.IncomingInvoiceState.PaymentProcessing,
        ];
      case IncomingInvoiceEnums.ViewState.Cancelled:
        return [
          ApiModels.IncomingInvoiceState.Denied,
          ApiModels.IncomingInvoiceState.DeniedAndHandled,
          ApiModels.IncomingInvoiceState.SentBack,
          ApiModels.IncomingInvoiceState.DeniedAndUnhandled,
        ];
      case IncomingInvoiceEnums.ViewState.Unhandled:
        return [ApiModels.IncomingInvoiceState.Unhandled, ApiModels.IncomingInvoiceState.SentForProcessing];
    }
  };
  const filter = (filter: ApiModels.IncomingInvoiceFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.Filter(filter);
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: IncomingInvoiceFormTypes.Filter) => {
    return filter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const reportToAPIFilter = (
    formFilter?: IncomingInvoiceFormTypes.Filter
  ): Omit<ApiModels.IncomingInvoiceFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      onlyMine: false,
      invoiceDateFrom: formFilter?.invoiceDate?.start,
      invoiceDateTo: formFilter?.invoiceDate?.end,
      dueDateFrom: formFilter?.dueDate?.start,
      dueDateTo: formFilter?.dueDate?.end,
      paymentDateFrom: formFilter?.paymentDate?.start,
      paymentDateTo: formFilter?.paymentDate?.end,
      snapshotDate: formFilter?.snapshotDate,
      supplierIds: formFilter?.suppliers?.map((x) => x.id as number),
      receiverIds: formFilter?.receivers?.map((x) => x.id as number),
      invoiceNumber: formFilter?.invoiceNumber,
      minTotalAmount: formFilter?.amount?.lowest,
      maxTotalAmount: formFilter?.amount?.highest,
      states: formFilter?.state
        ?.map((s) => ({ states: toApiStates(s.id as IncomingInvoiceEnums.ViewState) }))
        .flatMap((s) => s.states),
      paymentStatuses: formFilter?.paymentStatuses?.map((s) => s.id as ApiModels.IncomingInvoicePaymentStatus),
      dimensions: formFilter?.dimensions,
      accounts: formFilter?.bookingAccounts?.map((x) => x.id as number) ?? [],
      showAgentIncomingInvoices: formFilter?.agentIncomingInvoicesId,
    };
  };

  const getPaymentNumberTypes = (): AutocompleteListItem[] => {
    return [
      IncomingInvoiceEnums.PaymentNumberType.BG,
      IncomingInvoiceEnums.PaymentNumberType.PG,
      IncomingInvoiceEnums.PaymentNumberType.IBAN,
    ].map((key) => ({
      id: key,
      value: LocalizationHelperInstance.getFormattedMessage(`IncomingInvoice.PaymentNumberType_${key}`),
    }));
  };

  const getTodoState = (state: ApiModels.IncomingInvoiceState): string => {
    if (state === ApiModels.IncomingInvoiceState.Received) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.ToCertify);
    } else {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.ToSign);
    }
  };

  const getTodoStateLabelClass = (state: ApiModels.IncomingInvoiceState) => {
    if (state === ApiModels.IncomingInvoiceState.Received) {
      return cssExports.blueTag;
    } else {
      return cssExports.yellowTag;
    }
  };

  const markAsAlreadyPaid = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .IncomingInvoiceClient.MarkPaymentAsAlreadyPaid(id)
      .then((res) => res.data);
  };

  const checkPeriodisationAllowed = (req: ApiModels.CheckPeriodisationRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.CheckPeriodisation(req)
      .then((res) => res.data);
  };

  const editPeriodisation = (id: number, body: ApiModels.IncomingInvoicePeriodisationPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).IncomingInvoiceClient.EditPeriodisations(id, body);
  };

  const getIncomingInvoicesByTaxAgent = (taxAgent?: ApiModels.IncomingInvoiceSupplierInfo) => {
    if (taxAgent === undefined) return Promise.resolve();

    return filter({
      onlyMine: false,
      supplierId: taxAgent?.id,
    } as ApiModels.IncomingInvoiceFilter).then((res) => {
      return res.data.items;
    });
  };

  const getIncomingInvoicesByTaxAgentUrl = (taxAgent?: ApiModels.IncomingInvoiceSupplierInfo): string => {
    let url = "/Reports/Expenses/IncomingInvoices";

    if (taxAgent) {
      const taxAgentId = taxAgent.id.toString();
      const params = `?${RouteConstants.IncomingInvoiceQueryParams.SuppliersNames}=${RouteConstants.TaxAgentName}&${RouteConstants.IncomingInvoiceQueryParams.SuppliersIds}=${taxAgentId}`;

      url = url.concat(params);
    }

    return url;
  };

  const sendEmailToSupplier = (emailProperties: ApiModels.EmailProperties) => {
    return apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.SendEmailToSupplier(emailProperties);
  };

  const getSendSupplierEmailInfo = async (incomingInvoiceId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.GetSendSupplierEmailInfo(
      incomingInvoiceId
    );
    return res.data;
  };

  // Best-effort read backing the shared useCompanyInvoiceMail hook (supplier-invoice upload banner
  // + incoming-invoices report header). Uses showErrors:false so a failure (e.g. a company that
  // isn't found) never surfaces the global error toast on an opportunistic read the user didn't
  // explicitly ask for. Unlike the Admin-only full company settings, this endpoint is available to
  // anyone who can view or create a supplier invoice.
  const getCompanyInvoiceMail = async () => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: false,
    }).IncomingInvoiceClient.CompanyInvoiceMail();
    return res.data;
  };

  return {
    editableStates,
    get,
    save,
    create,
    certify,
    sign,
    onAccept,
    onAgingInvoiceSubmit,
    getAgingInvoiceApiModel,
    cancel,
    sendToPerson,
    sendBack,
    toggleAlreadyPaid,
    convertToGenericVoucher,
    getPdf,
    getAttachment,
    getIncomingInvoicePersons,
    getSuggestedIncomingInvoicePersons,
    getStateText,
    getPaymentStateText,
    getStateFlowText,
    getStateLogs,
    getTransactions,
    getSimilarInvoices,
    getDueIn,
    getOcrCollisions,
    getUIDisplayState,
    allowPeriodisations,
    getUpdatedInvoice,
    toViewState,
    getSuppliers,
    searchSuppliers,
    toApiStates,
    filter,
    reportFilter,
    reportToAPIFilter,
    getPaymentNumberTypes,
    getTodoState,
    getTodoStateLabelClass,
    markAsAlreadyPaid,
    checkPeriodisationAllowed,
    editPeriodisation,
    getIncomingInvoicesByTaxAgent,
    getIncomingInvoicesByTaxAgentUrl,
    sendEmailToSupplier,
    getSendSupplierEmailInfo,
    getCompanyInvoiceMail,
    getPeriodisationsAllowed,
    getUpdatedPaymentDetails,
    onUpdatePaymentDetails,
    getUpdatedSupplierPostalAddress,
    onUpdateSupplierPostalAddress,
  };
}
export default useIncomingInvoiceService;
